export const CONNECTION_LOST_ACTION = "connection_lost";

const STORAGE_KEY = "hg_pending_connection_loss_logs";
const MAX_PENDING_LOGS = 20;

export interface ConnectionLossLog {
  el_conv_id: string;
  el_agent_id: string;
  email?: string;
  user_id?: string;
  payload: string;
}

// keepalive lets the request outlive a closing tab, which supabase.functions.invoke can't do.
async function sendLog(log: ConnectionLossLog): Promise<void> {
  const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/logger`, {
    method: "POST",
    keepalive: true,
    headers: {
      "Content-Type": "application/json",
      apikey: anonKey,
      Authorization: `Bearer ${anonKey}`,
    },
    body: JSON.stringify({
      action: "conversation",
      data: { ...log, action: CONNECTION_LOST_ACTION, status: "fail" },
    }),
  });
  if (!response.ok) throw new Error(`logger responded ${response.status}`);
}

function readPendingLogs(): ConnectionLossLog[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function writePendingLogs(logs: ConnectionLossLog[]): void {
  try {
    if (logs.length === 0) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, JSON.stringify(logs.slice(-MAX_PENDING_LOGS)));
  } catch (error) {
    console.warn("[ConnectionLossLog] Could not store log for retry:", error);
  }
}

function keepForRetry(log: ConnectionLossLog): void {
  writePendingLogs([...readPendingLogs(), log]);
  window.addEventListener("online", () => void flushConnectionLossLogs(), { once: true });
}

/** A lost connection is often a lost network, so a log that can't be sent is kept and retried. */
export async function recordConnectionLoss(log: ConnectionLossLog): Promise<void> {
  try {
    await sendLog(log);
  } catch (error) {
    console.warn("[ConnectionLossLog] Send failed, keeping for retry:", error);
    keepForRetry(log);
  }
}

export async function flushConnectionLossLogs(): Promise<void> {
  const pending = readPendingLogs();
  if (pending.length === 0) return;

  writePendingLogs([]);
  await Promise.all(pending.map(recordConnectionLoss));
}
